import { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { BackTop, useScrollTop, Breadcrumb } from "@/components/feature/UI";
import { StandardCard, ListRow } from "@/components/feature/NewsCard";
import { news } from "@/mocks/news";
import { channels } from "@/mocks/categories";
import { useLdJson, useSeo, siteName } from "@/hooks/useSeo";
import { channelFill, channelIcon } from "@/utils/channel";

export default function ListPage() {
  useScrollTop();
  const [params, setParams] = useSearchParams();
  const category = params.get("category") ?? "";
  const query = (params.get("q") ?? "").trim();
  const [sort, setSort] = useState<"new" | "hot">("new");
  const [view, setView] = useState<"grid" | "list">("list");

  const channel = channels.find((c) => c.key === category);
  const searching = query.length > 0;

  const results = useMemo(() => {
    let list = [...news];
    if (channel) {
      list = list.filter((n) => n.category === channel.key);
    }
    if (searching) {
      const k = query.toLowerCase();
      list = list.filter(
        (n) =>
          n.title.toLowerCase().includes(k) ||
          n.summary.toLowerCase().includes(k) ||
          n.tags.some((t) => t.toLowerCase().includes(k)) ||
          n.author.toLowerCase().includes(k)
      );
    }
    list.sort((a, b) => (sort === "hot" ? b.reads - a.reads : b.date.localeCompare(a.date)));
    return list;
  }, [channel, searching, query, sort]);

  const title = searching
    ? `搜索：${query} - ${siteName}`
    : channel
    ? `${channel.name} - ${siteName}`
    : `全部资讯 - ${siteName}`;
  const description = searching
    ? `${siteName}中关于「${query}」的搜索结果，帮你快速定位新晋网红黑料、练习生爆料等娱乐新瓜。`
    : channel
    ? channel.desc
    : `${siteName}全部娱乐资讯速递，涵盖新晋网红黑料、小艺人黑历史、练习生爆料、新人演员翻车与素人走红黑幕。`;

  useSeo({ title, description });

  useLdJson(
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: channel ? channel.name : siteName,
      description,
      url: window.location.href,
      mainEntity: {
        "@type": "ItemList",
        itemListElement: results.slice(0, 10).map((n, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: window.location.origin + `/news/${n.id}`,
          name: n.title,
        })),
      },
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: window.location.origin + "/" },
          { "@type": "ListItem", position: 2, name: channel ? channel.name : "全部资讯" },
        ],
      },
    },
    "list-ld"
  );

  const setCategory = (key: string) => {
    const next = new URLSearchParams(params);
    if (key) next.set("category", key);
    else next.delete("category");
    next.delete("q");
    setParams(next);
  };

  return (
    <>
      <Header />
      <main>
        {channel ? (
          <section className="relative overflow-hidden border-b border-background-200">
            <div className="relative h-56 w-full md:h-64">
              <img src={channel.cover} alt={channel.name} title={channel.name} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/55 to-foreground-950/30"></div>
            </div>
            <div className="absolute inset-0 flex items-end">
              <div className="mx-auto w-full max-w-[1200px] px-4 pb-6 md:px-6">
                <div className="flex items-center gap-3 text-xs text-background-100/80">
                  <Link to="/" className="transition-colors hover:text-background-50">
                    首页
                  </Link>
                  <span>/</span>
                  <span className="text-background-50">{channel.name}</span>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <span className={`flex h-11 w-11 items-center justify-center rounded-md ${channelFill(channel.key)} text-background-50`}>
                    <i className={channelIcon(channel.key)}></i>
                  </span>
                  <div>
                    <h1 className="font-display text-2xl font-black text-background-50 md:text-3xl">{channel.name}</h1>
                    <p className="mt-1 text-sm text-background-100/85">{channel.tagline}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <section className="mx-auto max-w-[1200px] px-4 pt-8 md:px-6">
            <Breadcrumb items={[{ label: searching ? `搜索：${query}` : "全部资讯" }]} />
            <div className="mt-4">
              <h1 className="font-display text-2xl font-black text-foreground-950 md:text-3xl">
                {searching ? `「${query}」的搜索结果` : "全部资讯"}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-foreground-600">
                {searching
                  ? `为你找到 ${results.length} 条与「${query}」相关的内容。`
                  : "按栏目浏览新晋网红、小艺人、练习生、新人演员与素人相关的全部娱乐速递。"}
              </p>
            </div>
          </section>
        )}

        <section className="mx-auto max-w-[1200px] px-4 py-8 md:px-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {[{ key: "", name: "全部" }, ...channels].map((c) => (
                <button
                  key={c.key || "all"}
                  type="button"
                  onClick={() => setCategory(c.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    category === c.key
                      ? "bg-primary-500 font-medium text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  {c.name === "全部" ? "全部" : c.name}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-full border border-background-300 bg-background-50 p-1">
                {[
                  { k: "new", l: "最新", i: "ri-time-line" },
                  { k: "hot", l: "最热", i: "ri-fire-line" },
                ].map((s) => (
                  <button
                    key={s.k}
                    type="button"
                    onClick={() => setSort(s.k as "new" | "hot")}
                    className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-1 text-xs transition-colors ${
                      sort === s.k ? "bg-primary-100 font-medium text-primary-700" : "text-foreground-500 hover:text-foreground-700"
                    }`}
                  >
                    <i className={s.i}></i>
                    {s.l}
                  </button>
                ))}
              </div>
              <div className="flex rounded-full border border-background-300 bg-background-50 p-1">
                <button
                  type="button"
                  onClick={() => setView("grid")}
                  aria-label="网格视图"
                  className={`flex h-7 w-8 items-center justify-center rounded-full transition-colors ${
                    view === "grid" ? "bg-primary-100 text-primary-700" : "text-foreground-500"
                  }`}
                >
                  <i className="ri-layout-grid-line text-sm"></i>
                </button>
                <button
                  type="button"
                  onClick={() => setView("list")}
                  aria-label="列表视图"
                  className={`flex h-7 w-8 items-center justify-center rounded-full transition-colors ${
                    view === "list" ? "bg-primary-100 text-primary-700" : "text-foreground-500"
                  }`}
                >
                  <i className="ri-list-unordered text-sm"></i>
                </button>
              </div>
            </div>
          </div>

          {results.length === 0 ? (
            <div className="rounded-lg border border-background-200 bg-background-100/60 px-6 py-16 text-center">
              <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
              <h3 className="font-display mt-4 text-lg font-bold text-foreground-800">没有找到相关的新瓜</h3>
              <p className="mt-2 text-sm text-foreground-500">
                换个关键词，或返回全部资讯继续浏览。
              </p>
              <Link
                to="/channels"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                浏览全部资讯
              </Link>
            </div>
          ) : view === "grid" ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {results.map((n) => (
                <StandardCard key={n.id} item={n} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {results.map((n, i) => (
                <ListRow key={n.id} item={n} index={i} />
              ))}
            </div>
          )}

          {channel && (
            <div className="mt-10 rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6">
              <h3 className="font-display text-base font-bold text-foreground-950">关于「{channel.name}」栏目</h3>
              <p className="mt-2 text-sm leading-6 text-foreground-600">{channel.desc}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {results.slice(0, 4).map((n) => (
                  <Link
                    key={n.id}
                    to={`/news/${n.id}`}
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    {n.title.slice(0, 18)}…
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
      <BackTop />
    </>
  );
}